import {
  useGetConsultantBookingContractsQuery,
  useDeleteConsultantBookingContractMutation,
  GetConsultantBookingContracts
} from "@bonliva-crm/graphql";
import { Paper, Tooltip, Typography } from "@material-ui/core";
import {
  Table,
  MoreList,
  Color,
  Loader,
  ContractEditCheckIcon,
  CancelIcon
} from "@bonliva-crm/ui";
import { useState } from "react";
import locale from "@bonliva-crm/locale";
import { makeStyles } from "@material-ui/styles";
import { ModalProvider } from "./modal-provider";
import { ActionUnion, ContractUnion } from "../types";
import { entries } from "./utils/entries";
import { useSnackbars, useUserProfile } from "@hooks";
import cns from "classnames";

const headers = [
  { width: "40%", content: locale.Department },
  { width: "15%", content: locale.City },
  { width: "20%", content: locale.AgreementType },
  { width: "25%", content: locale.Date },
  { width: "10%", content: "" }
];

type Props = {
  consultantId: string;
  handleNavigationAction: (action: ActionUnion, contractId?: string) => void;
};

export const CustomerContracts = (props: Props) => {
  const userProfile = useUserProfile();
  const [openContractMoreList, setOpenContractMoreList] = useState(null);
  const [action, setAction] = useState<ActionUnion | null>(null);
  const [chosenContract, setChosenContract] = useState<ContractUnion | null>(null);
  const classes = useStyles();
  const { showError, showSuccess } = useSnackbars();

  const query = useGetConsultantBookingContractsQuery({
    variables: {
      id: props.consultantId
    },
    fetchPolicy: "network-only"
  });

  const [deleteConsultantBookingContractMutation] =
    useDeleteConsultantBookingContractMutation({
      onCompleted: () => {
        showSuccess(locale.deleteContractSuccess);
      },
      onError: (error) => {
        console.error(error);
        showError(locale.cannotPerformAction);
      },
      refetchQueries: [GetConsultantBookingContracts.OperationName]
    });

  const handleDelete = (id: string) => {
    deleteConsultantBookingContractMutation({
      variables: {
        contractId: id
      },
      optimisticResponse: {
        deleteConsultantBookingContract: {
          _id: id,
          __typename: "ConsultantBookingContract"
        }
      }
    });
  };

  if (query.loading && !query.data) {
    return <Loader />;
  }

  if (query.data && query?.data?.getConsultantBookingContracts.length === 0) {
    return (
      <Paper className={classes.paperStyle}>
        <Typography variant="body2" className={classes.margin16}>
          {locale.NoCustomerContracts}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Typography variant="h5" className={classes.margin16}>
        {locale.CustomerContracts}
      </Typography>
      <Table
        headers={headers}
        rows={query.data.getConsultantBookingContracts.map((contract) => [
          contract.CustomerName,
          contract.CustomerCity,
          <span className={classes.flexLeft}>
            {locale[contract.Type]}
            {contract.IsSigned ? (
              <ContractEditCheckIcon htmlColor={Color.CheckGreen} />
            ) : undefined}
            {contract.IsNullified && (
              <div>
                <CancelIcon color="error" />
              </div>
            )}
          </span>,
          <Tooltip title={contract.Dates} placement="bottom">
            <span className={cns(classes.width100, classes.twoRowsOnly)}>
              {contract.Dates}
            </span>
          </Tooltip>,
          openContractMoreList === contract._id ? (
            <MoreList
              clickAway={() => setOpenContractMoreList(null)}
              entries={entries({
                closeList: () => setOpenContractMoreList(null),
                setAction: (a: ActionUnion) => setAction(a),
                contract: contract,
                showError: showError,
                handleNavigationAction: props.handleNavigationAction,
                customerContract: true,
                disabledDelete: !userProfile.hasPermission(
                  "CanDeleteConsultantBookingContract"
                )
              })}
            />
          ) : undefined,
          () => {
            if (openContractMoreList === contract._id) {
              setOpenContractMoreList(null);
              setChosenContract(null);
              return;
            }
            setOpenContractMoreList(contract._id);
            setChosenContract(contract);
          }
        ])}
      />
      <ModalProvider
        open={!!action}
        setOpen={() => setAction(null)}
        action={action}
        contract={chosenContract}
        contracts={query.data?.getConsultantBookingContracts}
        consultantId={props.consultantId}
        handleDelete={handleDelete}
        customerContract
      />
    </>
  );
};

const useStyles = makeStyles(() => ({
  margin16: {
    marginTop: 16,
    marginBottom: 16
  },
  width100: {
    width: "100%"
  },
  paperStyle: {
    marginTop: 16,
    width: "100%",
    height: "190px",
    borderRadius: "2px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  },
  twoRowsOnly: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
    maxWidth: "17.5rem"
  },
  flexLeft: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  }
}));
